import styled from "@emotion/styled";
import { FlexRow, H2, HeadingGroup, Link, List } from "atoms";
import { HomePageDataShape } from "features/hub/api/useHomePageData";
import { ReviewableCard } from "features/reviewable";

const colors = ["accent3LL", "accent2LL", "accent4LL", "accent1LL"] as const;

type HighlightBlockProps = Omit<
  HomePageDataShape["highlightBlocks"][number],
  "ids"
> & {
  index?: number;
  reverse?: boolean;
  // NOTE: must be index of gradients
  color?: 0 | 1 | 2 | 3;
};

function HighlightBlock({
  logo,
  heading,
  subheading,
  cta,
  url,
  entities,
  index = 1,
  color,
  reverse,
}: HighlightBlockProps) {
  // Reverse the flex order based on props. If reverse is requested, use it.
  // Otherwise, determine the index of this component within the array and alternate.
  const isReverse = reverse ?? index % 2 === 0;

  // Get colors based on props. If a color is requested, use it.
  // Otherwise, determine the index of this component within the array and alternate through the
  // three options listed above.
  const colorIndex = color ?? index % 3;
  const blockColor = colors[colorIndex];

  return (
    <Wrapper isReverse={isReverse} color={blockColor}>
      <TitleBlock>
        <HeadingGroup>
          <FlexRow as="small" gap=".5em">
            {logo && <Logo src={logo?.url} alt={logo?.altText || ""} />}
            {heading}
          </FlexRow>
          <H2 size="xxl">{subheading}</H2>
        </HeadingGroup>
        <Link to={url} variant="primary" size="xs" arrow="r">
          {cta}
        </Link>
      </TitleBlock>

      <Profiles type="blank">
        {entities.map((entity) => (
          <li key={entity.id}>
            <ReviewableCard {...entity} />
          </li>
        ))}
      </Profiles>
    </Wrapper>
  );
}

const Wrapper = styled.section<{ isReverse: boolean; color: string }>(
  ({ color, isReverse, theme }) => ({
    position: "relative",
    [theme.breakpoints.desktopMedium]: {
      display: "grid",
      gap: theme.spacing.gap,
      gridTemplateColumns: isReverse
        ? `calc(75% - (${theme.spacing.gap} / 4)) 1fr`
        : `1fr calc(75% - (${theme.spacing.gap} / 4))`,
      gridTemplateAreas: isReverse ? `"profiles title"` : `"title profiles"`,
    },
    "&:before": {
      aspectRatio: "1",
      backgroundColor: theme.colors[color],
      borderRadius: theme.borderRadius.round,
      content: "''",
      top: 0,
      left: 0,
      width: "120%",
      transform: "translate(-20%, -37%)",
      transformOrigin: "center",
      pointerEvents: "none",
      position: "absolute",
      zIndex: 0,
      [theme.breakpoints.tablet]: {
        width: "60%",
        transform: "translate(-15%, -37%)",
        ...(isReverse && {
          left: "auto",
          right: 0,
          transform: "translate(30%, -23%)",
        }),
      },
      [theme.breakpoints.desktop]: {
        width: "30%",
        transform: "scale(1.5)",
        transformOrigin: "70% 105%",
        ...(isReverse && {
          transformOrigin: "50% 70%",
        }),
      },
    },
  })
);

const TitleBlock = styled(FlexRow)(({ theme }) => ({
  gridArea: "title",
  padding: `${theme.spacing.gap} 0`,
  position: "relative",
  justifyContent: "space-between",
  alignItems: "flex-end",
  flex: 1,
  gap: theme.spacing.gap,
  [theme.breakpoints.desktopMedium]: {
    alignItems: "flex-start",
    flexDirection: "column",
    justifyContent: "center",
    "& *": {
      position: "relative",
      zIndex: 1,
    },
  },
}));

const Logo = styled.img({
  height: 40,
  maxHeight: 40,
  maxWidth: "35%",
});

const Profiles = styled(List)(({ theme }) => ({
  gridArea: "profiles",
  display: "flex",
  gap: theme.spacing.gap,
  flexDirection: "column",
  position: "relative",
  zIndex: 1,
  [theme.breakpoints.tablet]: {
    flexDirection: "row",
    "&>*": {
      display: "flex",
      flex: 1,
    },
  },
}));
export { HighlightBlock };
